import { supabase } from '../lib/supabase';

/**
 * Event Service - Handles all event-related database operations
 * Converts between database snake_case and React camelCase
 */

/**
 * Helper function to extract valid image URL from images array
 * Handles both formats:
 * 1. Plain URL strings: ["https://example.com/image.jpg"]
 * 2. JSON objects: ["{\"id\":123,\"url\":\"https://example.com/image.jpg\"}"]
 */
const getFirstImageUrl = (images) => {
  if (!images || !Array.isArray(images) || images?.length === 0) {
    return null;
  }

  const firstImage = images?.[0];
  
  // If it's already a URL string, return it
  if (typeof firstImage === 'string' && firstImage?.startsWith('http')) {
    return firstImage;
  }

  // If it's a JSON string, parse it and extract URL
  if (typeof firstImage === 'string' && firstImage?.startsWith('{')) {
    try {
      const parsed = JSON.parse(firstImage);
      return parsed?.url || null;
    } catch (e) {
      console.error('Failed to parse image JSON:', e);
      return null;
    }
  }

  // If it's already an object with URL property
  if (typeof firstImage === 'object' && firstImage?.url) {
    return firstImage?.url;
  }

  return null;
};

/**
 * Helper function to extract all image URLs from images array
 * Handles both formats and returns array of {id, url, alt} objects
 */
const parseAllImages = (images, eventTitle = '', eventType = '') => {
  if (!images || !Array.isArray(images) || images?.length === 0) {
    return [];
  }

  return images?.map((image, index) => {
    let url = null;
    let id = null;
    
    // If it's a plain URL string
    if (typeof image === 'string' && image?.startsWith('http')) {
      url = image;
      id = index + 1;
    }
    // If it's a JSON string
    else if (typeof image === 'string' && image?.startsWith('{')) {
      try {
        const parsed = JSON.parse(image);
        url = parsed?.url || null;
        id = parsed?.id || index + 1;
      } catch (e) {
        console.error('Failed to parse image JSON:', e);
        return null;
      }
    }
    // If it's already an object
    else if (typeof image === 'object' && image?.url) {
      url = image?.url;
      id = image?.id || index + 1;
    }

    if (!url) return null;

    return {
      id,
      url,
      alt: `${eventTitle} - ${eventType} etkinlik fotoğrafı ${index + 1}`
    };
  })?.filter(Boolean); // Remove null entries
};

// Helper function to convert database row to camelCase
const dbToEvent = (row) => ({
  id: row?.id,
  organizerId: row?.organizer_id,
  title: row?.title,
  description: row?.description,
  date: row?.event_date,
  time: row?.event_time,
  duration: row?.duration,
  difficulty: row?.difficulty,
  eventType: row?.event_type,
  status: row?.status,
  location: row?.location,
  city: row?.city,
  latitude: row?.latitude,
  longitude: row?.longitude,
  priceOwnBoard: parseFloat(row?.price_own_board),
  priceWithRental: parseFloat(row?.price_with_rental),
  priceLifeVest: parseFloat(row?.price_life_vest) || 0,
  priceSupMotor: parseFloat(row?.price_sup_motor) || 0,
  priceWaterproofBag: parseFloat(row?.price_waterproof_bag) || 0,
  maxParticipants: row?.max_participants,
  minParticipants: row?.min_participants,
  registrationDeadline: row?.registration_deadline,
  groupDiscount: row?.group_discount,
  earlyBirdDiscount: row?.early_bird_discount,
  cancellationInsurance: row?.cancellation_insurance,
  waitingList: row?.waiting_list,
  autoApprove: row?.auto_approve,
  sendReminders: row?.send_reminders,
  requiredEquipment: row?.required_equipment || {},
  skillLevel: row?.skill_level,
  weatherConditions: row?.weather_conditions || {},
  images: row?.images || [],
  custom_options: row?.custom_options || [],
  customOptionsType: row?.custom_options_type || 'multiple',
  // Ticket option toggles (default true for backward compat)
  allowOwnBoard: row?.allow_own_board !== false,
  allowRentalBoard: row?.allow_rental_board !== false,
  allowStandardTicket: row?.allow_standard_ticket === true,
  priceStandardTicket: parseFloat(row?.price_standard_ticket) || 0,
  // Add image property for backward compatibility and easy access
  image: getFirstImageUrl(row?.images),
  // Add heroImage for event details page
  heroImage: getFirstImageUrl(row?.images),
  // Add default alt text
  imageAlt: `${row?.title} - ${row?.event_type} etkinlik görseli`,
  heroImageAlt: `${row?.title} - ${row?.event_type} etkinlik görseli`,
  // Add parsed photos array for PhotoGallery component
  photos: parseAllImages(row?.images, row?.title, row?.event_type),
  attendeeCount: row?.attendee_count || 0,
  // Add distance if available (from nearby queries)
  distance: row?.distance_meters,
  distanceKm: row?.distance_meters ? (row?.distance_meters / 1000)?.toFixed(2) : null,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at
});

// Helper function to convert React camelCase to database snake_case
const eventToDb = (event) => {
  const safeInt = (val) => {
    const parsed = parseInt(val);
    return isNaN(parsed) ? undefined : parsed;
  };
  const safeFloat = (val) => {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? 0 : parsed;
  };

  const db = {
    title: event?.title,
    description: event?.description,
    event_date: event?.date,
    event_time: event?.time,
    duration: safeInt(event?.duration),
    difficulty: event?.difficulty || undefined,
    event_type: event?.eventType || undefined,
    // Use the explicit status from the event object; never default to 'draft' on update
    status: event?.status !== undefined ? event?.status : 'published',
    location: event?.location,
    city: event?.city || 'istanbul',
    price_own_board: safeFloat(event?.priceOwnBoard),
    price_with_rental: safeFloat(event?.priceWithRental),
    price_life_vest: safeFloat(event?.priceLifeVest),
    price_sup_motor: safeFloat(event?.priceSupMotor),
    price_waterproof_bag: safeFloat(event?.priceWaterproofBag),
    max_participants: safeInt(event?.maxParticipants),
    min_participants: safeInt(event?.minParticipants) ?? 1,
    registration_deadline: event?.registrationDeadline || undefined,
    group_discount: event?.groupDiscount || false,
    early_bird_discount: event?.earlyBirdDiscount || false,
    cancellation_insurance: event?.cancellationInsurance || false,
    waiting_list: event?.waitingList !== undefined ? event?.waitingList : true,
    auto_approve: event?.autoApprove !== undefined ? event?.autoApprove : true,
    send_reminders: event?.sendReminders !== undefined ? event?.sendReminders : true,
    required_equipment: event?.requiredEquipment || {},
    skill_level: event?.skillLevel || 'beginner',
    weather_conditions: event?.weatherConditions || {},
    images: event?.images || [],
    custom_options: event?.customOptions || [],
    custom_options_type: event?.customOptionsType || 'multiple',
    allow_own_board: event?.allowOwnBoard !== false,
    allow_rental_board: event?.allowRentalBoard !== false,
    allow_standard_ticket: event?.allowStandardTicket === true,
    price_standard_ticket: safeFloat(event?.priceStandardTicket) || 0,
    desc_own_board: event?.descOwnBoard || null,
    desc_rental_board: event?.descRentalBoard || null,
    desc_standard_ticket: event?.descStandardTicket || null
  };

  // Only include latitude/longitude if they have actual values to avoid overwriting with null
  if (event?.latitude !== undefined && event?.latitude !== '' && event?.latitude !== null) {
    db.latitude = parseFloat(event?.latitude);
  }
  if (event?.longitude !== undefined && event?.longitude !== '' && event?.longitude !== null) {
    db.longitude = parseFloat(event?.longitude);
  }

  return db;
};

export const eventService = {
  /**
   * Get all published events (public access)
   */
  async getAll() {
    const { data, error } = await supabase
      ?.from('events')
      ?.select('*')
      ?.eq('status', 'published')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(dbToEvent);
  },

  /**
   * Get all events for admin (includes all statuses)
   */
  async getAllForAdmin() {
    const { data, error } = await supabase
      ?.from('events')
      ?.select(`
        *,
        organizer:user_profiles!organizer_id(full_name, email)
      `)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(row => ({
      ...dbToEvent(row),
      organizerName: row?.organizer?.full_name,
      organizerEmail: row?.organizer?.email
    }));
  },

  /**
   * Get events by organizer (authenticated)
   */
  async getByOrganizer(organizerId) {
    const { data, error } = await supabase
      ?.from('events')
      ?.select('*')
      ?.eq('organizer_id', organizerId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(dbToEvent);
  },

  /**
   * Get single event by ID
   */
  async getById(eventId) {
    const { data, error } = await supabase
      ?.from('events')
      ?.select('*')
      ?.eq('id', eventId)
      ?.single();

    if (error) throw error;
    return dbToEvent(data);
  },

  /**
   * Create new event (must be authenticated)
   */
  async create(eventData) {
    // Get current user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert to database format
    const dbEvent = eventToDb(eventData);

    // Insert with organizer_id
    const { data, error } = await supabase
      ?.from('events')
      ?.insert({
        ...dbEvent,
        organizer_id: user?.id
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Update location_geo using PostGIS if coordinates provided
    if (eventData?.latitude && eventData?.longitude) {
      const lat = parseFloat(eventData?.latitude);
      const lng = parseFloat(eventData?.longitude);
      
      try {
        await supabase?.rpc('exec_sql', {
          sql: `UPDATE events SET location_geo = ST_SetSRID(ST_MakePoint(${lng}, ${lat}), 4326)::geography WHERE id = '${data?.id}'`
        });
      } catch (geoError) {
        console.warn('Could not update location_geo directly, will be synced by database');
      }
    }

    return dbToEvent(data);
  },

  /**
   * Update existing event (must be owner or admin)
   */
  async update(eventId, eventData) {
    // Get current user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert to database format
    const dbEvent = eventToDb(eventData);

    // Remove undefined values to prevent overwriting existing DB fields with null
    const cleanDbEvent = Object.fromEntries(
      Object.entries(dbEvent)?.filter(([, v]) => v !== undefined)
    );

    // Update (RLS ensures user owns the event or is admin)
    const { data, error } = await supabase
      ?.from('events')
      ?.update(cleanDbEvent)
      ?.eq('id', eventId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Update location_geo using PostGIS if coordinates changed
    if (eventData?.latitude && eventData?.longitude) {
      const lat = parseFloat(eventData?.latitude);
      const lng = parseFloat(eventData?.longitude);
      
      try {
        await supabase?.rpc('exec_sql', {
          sql: `UPDATE events SET location_geo = ST_SetSRID(ST_MakePoint(${lng}, ${lat}), 4326)::geography WHERE id = '${eventId}'`
        });
      } catch (geoError) {
        console.warn('Could not update location_geo directly, will be synced by database');
      }
    }

    return dbToEvent(data);
  },

  /**
   * Update event status (admin only)
   */
  async updateStatus(eventId, newStatus) {
    const { data, error } = await supabase
      ?.from('events')
      ?.update({ status: newStatus })
      ?.eq('id', eventId)
      ?.select()
      ?.single();

    if (error) throw error;
    return dbToEvent(data);
  },

  /**
   * Delete event (must be owner or admin)
   */
  async delete(eventId) {
    const { error } = await supabase
      ?.from('events')
      ?.delete()
      ?.eq('id', eventId);

    if (error) throw error;
  },

  /**
   * Publish event (change status to published)
   */
  async publish(eventId) {
    // Get current user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('events')
      ?.update({ status: 'published' })
      ?.eq('id', eventId)
      ?.eq('organizer_id', user?.id)
      ?.select()
      ?.single();

    if (error) throw error;
    return dbToEvent(data);
  },

  /**
   * Get nearby events using PostGIS spatial query
   * @param {number} lat - Latitude of search center
   * @param {number} lng - Longitude of search center
   * @param {number} radius - Search radius in meters (default: 10000m = 10km)
   * @returns {Promise<Array>} Array of nearby published events sorted by distance
   */
  async getNearby(lat, lng, radius = 10000) {
    try {
      // Call the PostGIS RPC function created in migration
      const { data, error } = await supabase
        ?.rpc('get_nearby_events', {
          lat: parseFloat(lat),
          long: parseFloat(lng),
          radius_meters: parseInt(radius)
        });

      if (error) throw error;

      // Convert to camelCase using existing helper
      return data?.map(dbToEvent);
    } catch (error) {
      console.error('Error fetching nearby events:', error);
      throw new Error(error?.message || 'Yakındaki etkinlikler yüklenirken bir hata oluştu');
    }
  }
};